import { Button, Grid, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

import PageWrapper from 'components/Common/PageWraper';
import PageHeader from 'components/Header/Page';
import KpiTileGroup from 'components/Widgets/KpiTileGroup';
import DashboardCard from 'components/Widgets/Distribution Card';

function KpiTilePage() {
  const navigate = useNavigate();
  const [expandedKey, setExpandedKey] = useState<string>('size');
  const [isMaximized, setIsMaximized] = useState<boolean>(false);

  function formatCompactNumber(value: number): { value: number; unit?: string } {
    if (value >= 1_000_000) return { value: value / 1_000_000, unit: 'M' };
    if (value >= 1_000) return { value: value / 1_000, unit: 'K' };
    return { value };
  }

  /* ======================
     DASHBOARD DATA
  ====================== */

  const SIZE_TABS = [
    {
      key: 'stored',
      label: 'Data Stored',
      showUnitToggle: true,
      tooltip: 'This tab shows the distribution of data stored across different platforms.',
      charts: [
        {
          chartType: 'pie' as const,
          data: [
            { name: 'ADLS', value: 102088012 },
            { name: 'Teradata', value: 52198367 },
            { name: 'Azure SQL', value: 41531345 },
            { name: 'Synapse', value: 42986456 },
            { name: 'Cosmos', value: 46234523 },
          ],
          onSliceClick: (item: any) => console.log('Pie slice clicked:', item),
        },
      ],
    },
    {
      key: 'trend',
      label: 'Data Trends',
      showUnitToggle: false,
      tooltip: 'This tab illustrates the trends in data usage over time.',
      charts: [
        {
          chartType: 'donut' as const,
          title: 'Data Trends Chart',
          data: [
            { name: 'Azure', value: 20 },
            { name: 'Synapse', value: 12 },
            { name: 'Teradata', value: 10 },
          ],
          onSliceClick: (item: any) => console.log('Donut slice clicked:', item),
        },
      ],
    },
  ];

  const PROCESSING_TABS = [
    {
      key: 'jobs',
      label: 'ADF Jobs',
      showUnitToggle: false,
      tooltip: 'Year-to-date execution distribution across processing platforms.',
      charts: [
        {
          chartType: 'donut' as const,
          title: 'Pipeline Execution Split',
          data: [
            { name: 'ADF Pipelines', value: 12550 },
            { name: 'Teradata Jobs', value: 8300 },
            { name: 'Synapse Pipelines', value: 4100 },
            { name: 'Databricks Jobs', value: 2750 },
          ],
          onSliceClick: (item: any) => console.log('Processing job clicked:', item),
        },
      ],
    },

    {
      key: 'cluster-ytd',
      label: 'Data Cluster YTD',
      showUnitToggle: false,
      tooltip: 'Year-to-date workload distribution across data clusters.',
      charts: [
        {
          chartType: 'pie' as const,
          data: [
            { name: 'Synapse Dedicated', value: 310 },
            { name: 'Azure SQL', value: 185 },
            { name: 'Cosmos DB', value: 150 },
            { name: 'Teradata', value: 95 },
          ],
          onSliceClick: (item: any) => console.log('Cluster slice clicked:', item),
        },
      ],
    },
  ];

  const { value: adfValue, unit: adfUnit } = formatCompactNumber(12550);
  const { value: clusterValue, unit: clusterUnit } = formatCompactNumber(14550000);
  const { value: pbiValue, unit: pbiUnit } = formatCompactNumber(97000);

  return (
    <PageWrapper>
      <PageHeader title="KPI Tile Widgets">
        <Button variant="pepwiseSecondary" onClick={() => navigate(-1)}>
          ← Back
        </Button>
      </PageHeader>

      <Grid container spacing={2} mt={2} alignItems="flex-start">
        {/* ================= LEFT : EXPANDED DASHBOARD ================= */}
        <Grid item xs={12} md={!isMaximized ? 8 : 12}>
          {expandedKey === 'size' && (
            <DashboardCard
              title="Size"
              hasTooltip
              tooltipLabel="This card displays a configurable pie or donut distribution with tabs."
              showUnits
              tabs={SIZE_TABS}
              isMaximized={isMaximized}
              isExpanded={true}
              exportCallback={() => alert('Export API call should be made')}
              onToggleExpand={() => setIsMaximized(!isMaximized)}
            />
          )}

          {expandedKey === 'processing' && (
            <DashboardCard
              title="Processing"
              hasTooltip
              tooltipLabel="Processing insights"
              showUnits={false}
              tabs={PROCESSING_TABS}
              isMaximized={isMaximized}
              isExpanded={true}
              exportCallback={() => alert('Export API call should be made')}
              onToggleExpand={() => setIsMaximized(!isMaximized)}
            />
          )}
        </Grid>

        {/* ================= RIGHT : KPI TILE GROUPS ================= */}
        {
          !isMaximized &&
          <Grid item xs={12} md={4}>
            <Stack spacing={2}>
              {expandedKey !== 'size' && (
                <KpiTileGroup
                  title="Size"
                  hasTooltip
                  tooltipLabel="Size KPIs"
                  columns={2}
                  isLoading={false}
                  showExpandIcon
                  onExpand={() => setExpandedKey('size')}
                  tiles={[
                    {
                      id: 'data-stored',
                      title: 'Data Stored',
                      number: 50.6,
                      units: 'PB',
                      hasTooltip: true,
                      tooltipLabel: [
                        {
                          title: 'Data Stored In Teradata (PROD) & Azure (All Environments)',
                          description: (
                            <>
                              <i>Data Available:</i>{' '}
                              <b>From 2023-12-12 To 2024-12-12</b>
                            </>
                          ),
                        },
                      ],
                    },
                    {
                      id: 'data-trend',
                      title: 'Data Trend',
                      number: 0.11,
                      units: '%',
                      unitIcon: 'upArrow',
                      unitIconColor: 'red',
                    },
                  ]}
                />
              )}

              {expandedKey !== 'processing' && (
                <KpiTileGroup
                  title="Processing"
                  hasTooltip
                  tooltipLabel="Processing KPIs"
                  columns={2}
                  isLoading={false}
                  showExpandIcon
                  onExpand={() => setExpandedKey('processing')}
                  tiles={[
                    {
                      id: 'adf-jobs',
                      title: 'ADF Pipelines & Teradata Jobs',
                      number: adfValue,
                      units: adfUnit,
                      hasTooltip: true,
                      tooltipLabel: [
                        {
                          title: 'ADF Pipelines',
                          description:
                            'Total Azure Data Factory pipelines executed during the selected period',
                        },
                        {
                          title: 'Teradata Jobs',
                          description:
                            'Number of Teradata batch jobs processed successfully',
                        },
                      ],
                    },
                    {
                      id: 'data-cluster-ytd',
                      title: 'Data Cluster YTD',
                      number: clusterValue,
                      units: clusterUnit,
                    },
                  ]}
                />
              )}

              <KpiTileGroup
                title="BI Usage"
                hasTooltip
                tooltipLabel="Business Intelligence usage across tools"
                columns={2}
                isLoading={false}
                showExpandIcon={false}
                tiles={[
                  { id: 'powerbi', title: 'Power BI', number: pbiValue, units: pbiUnit },
                  { id: 'powerbi-trend', title: 'Power BI Trends', number: 0, units: '' },
                ]}
              />

              <KpiTileGroup
                title="Sustain YTD"
                hasTooltip
                tooltipLabel="Unplanned downtime metrics"
                columns={2}
                isLoading={false}
                showExpandIcon={false}
                tiles={[
                  {
                    id: 'unplanned-platform-downtime',
                    title: 'Unplanned Platform Downtime',
                    number: [1, 2, 46],
                    units: ['Days', 'Hours', 'Minutes'],
                  },
                ]}
              />
            </Stack>
          </Grid>
        }
      </Grid>
    </PageWrapper>
  );
}

export default KpiTilePage;
